import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, PhoneCall } from 'lucide-react';
import { toast } from 'sonner';
import { AWAY_STATUSES, PRESENCE_LABELS, type PresenceState } from '#shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { timeAgo } from '../lib/format';
import { Card, cx } from './ui';

interface Person {
  id: string; name: string; designation: string | null; state: PresenceState; since: string | null;
  lastCallAt: string | null; callsToday: number; talkSecondsToday: number; checkedInAt: string | null; checkedOutAt: string | null;
}

const AWAY = Object.fromEntries(AWAY_STATUSES.map((a) => [a.key, a]));
export function presenceLabel(state: PresenceState): { label: string; color: string; emoji?: string } {
  if (state in PRESENCE_LABELS) return PRESENCE_LABELS[state as keyof typeof PRESENCE_LABELS];
  const a = AWAY[state];
  return { label: a?.label ?? state, color: '#7c3aed', emoji: a?.emoji };
}

/** "for 12 min", "for 1 h 5 min". */
function forHowLong(since: string | null): string {
  if (!since) return '';
  const mins = Math.max(0, Math.round((Date.now() - new Date(since).getTime()) / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `for ${mins} min`;
  return `for ${Math.floor(mins / 60)} h${mins % 60 ? ` ${mins % 60} min` : ''}`;
}
const talk = (s: number) => (s < 60 ? `${s}s` : s < 3600 ? `${Math.round(s / 60)} min` : `${Math.floor(s / 3600)} h ${Math.round((s % 3600) / 60)} min`);

/** In the menu: tell the team you're at lunch, in a meeting… with one tap. */
export function MyStatus() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const me = useQuery({ queryKey: ['presence', 'me'], queryFn: () => api.get<{ state: PresenceState; since: string | null }>('/presence/me'), refetchInterval: 60_000 });
  const set = useMutation({
    mutationFn: (status: string | null) => api.put<{ state: PresenceState }>('/presence/away', { status }),
    onSuccess: (r) => { qc.setQueryData(['presence', 'me'], r); void qc.invalidateQueries({ queryKey: ['team-status'] }); setOpen(false); },
    onError: (e) => toast.error(e.message),
  });
  const state = me.data?.state ?? 'online';
  const away = state in AWAY;
  const l = presenceLabel(state === 'idle' || state === 'offline' ? 'online' : state);
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800">
        <span className="size-2.5 shrink-0 rounded-full" style={{ background: l.color }} aria-hidden />
        <span className="flex-1 truncate text-left">{l.emoji ? `${l.emoji} ` : ''}{state === 'on_call' ? 'On a call' : away ? l.label : 'Available'}</span>
        <ChevronDown className="size-4 text-slate-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute bottom-full left-0 z-20 mb-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900">
            <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800" onClick={() => set.mutate(null)}>
              <span className="size-2.5 rounded-full bg-emerald-500" />Available
            </button>
            {AWAY_STATUSES.map((a) => (
              <button key={a.key} className={cx('flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800', state === a.key && 'font-semibold')}
                onClick={() => set.mutate(a.key)}>
                <span aria-hidden>{a.emoji}</span>{a.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** On Home, for managers: who's on a call, away, working, idle or offline — refreshed every 30 seconds. */
export function TeamRightNow() {
  const { can } = useAuth();
  const team = useQuery({
    queryKey: ['team-status'],
    queryFn: () => api.get<{ people: Person[]; counts: Record<string, number> }>('/team/status'),
    enabled: can('attendance.view_team'),
    refetchInterval: 30_000,
  });
  if (!can('attendance.view_team') || !team.data || team.data.people.length < 2) return null;
  const { people, counts } = team.data;
  const awayCount = AWAY_STATUSES.reduce((n, a) => n + (counts[a.key] ?? 0), 0);
  return (
    <Card className="mt-6">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 p-5 pb-3">
        <h2 className="font-semibold">Team right now</h2>
        {([['on_call', counts.on_call], ['online', counts.online], ['away', awayCount], ['idle', counts.idle], ['offline', counts.offline]] as const).map(([k, n]) => (
          <span key={k} className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400">
            <span className="size-2.5 rounded-full" style={{ background: k === 'away' ? '#7c3aed' : PRESENCE_LABELS[k].color }} />
            <b className="text-slate-900 dark:text-white">{n ?? 0}</b> {k === 'away' ? 'away' : PRESENCE_LABELS[k].label.toLowerCase()}
          </span>
        ))}
      </div>
      <ul className="grid gap-px bg-slate-100 sm:grid-cols-2 lg:grid-cols-3 dark:bg-slate-800">
        {people.map((p) => {
          const l = presenceLabel(p.state);
          return (
            <li key={p.id} className="flex items-center gap-3 bg-white px-5 py-3 dark:bg-slate-900">
              <span className={cx('size-3 shrink-0 rounded-full', p.state === 'on_call' && 'animate-pulse')} style={{ background: l.color }} aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{p.name}</p>
                <p className="truncate text-sm" style={{ color: l.color }}>
                  {p.state === 'on_call' && <PhoneCall className="mr-1 inline size-3.5" />}{l.emoji ? `${l.emoji} ` : ''}{l.label}
                  <span className="text-slate-500"> {p.state === 'offline' ? (p.since ? `· seen ${timeAgo(p.since)}` : '· not seen yet') : forHowLong(p.since)}</span>
                </p>
              </div>
              <div className="text-right text-xs text-slate-500">
                <p><b className="text-slate-800 dark:text-slate-200">{p.callsToday}</b> calls</p>
                {p.talkSecondsToday > 0 && <p>{talk(p.talkSecondsToday)} talk</p>}
                {!p.checkedInAt && <p className="text-amber-600">Not checked in</p>}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
